import React from 'react';
import { Navigate } from 'react-router-dom';
import { useCmmsAuth } from '../contexts/CmmsAuthContext';
import { ShieldOff } from 'lucide-react';

/**
 * Permission guard component that controls access to protected content.
 *
 * @param {string} permission - Single permission required
 * @param {string[]} permissions - Multiple permissions (any of them)
 * @param {string[]} allPermissions - Multiple permissions (all required)
 * @param {React.ReactNode} fallback - Custom fallback component
 * @param {string} redirectTo - Redirect path if no access
 * @param {React.ReactNode} children - Protected content
 */
const PermissionGuard = ({
  permission,
  permissions,
  allPermissions,
  fallback,
  redirectTo,
  children
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading } = useCmmsAuth();

  // Company owners bypass CMMS permission checks — they have full access
  const companyRole = localStorage.getItem('companyRole');
  if (companyRole === 'COMPANY_OWNER') {
    return children;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  let hasAccess = true;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions) {
    hasAccess = hasAnyPermission(...permissions);
  } else if (allPermissions) {
    hasAccess = hasAllPermissions(...allPermissions);
  }

  if (!hasAccess) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }
    if (fallback) {
      return fallback;
    }
    return <AccessDenied />;
  }

  return children;
};

/**
 * Default access denied component
 */
const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center h-64 text-center">
    <ShieldOff className="w-16 h-16 text-gray-300 mb-4" />
    <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
    <p className="text-gray-500 mt-2">You don't have permission to access this page.</p>
  </div>
);

export default PermissionGuard;
